import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, Input, PageTitle } from "@/components/ui/primitives";
import { DataState } from "@/components/data-state";
import { Pagination } from "@/components/pagination";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useAuth } from "@/hooks/use-auth";
import { useI18n } from "@/i18n";
import type { ListParams } from "@/api/endpoints";
import type { ListResult } from "@/types";

const PAGE_SIZE = 25;

/**
 * The searchable, paged list shared by companies, products, samples and tasks.
 *
 * Those four differ only in what a row looks like, so the filter state, the
 * debounce, the query key and the pagination live here once. Anything with a
 * genuinely different shape should be its own page rather than gain a flag.
 */
export function RecordListPage<T>({
  title,
  resource,
  fetcher,
  renderRow,
  action,
}: {
  title: string;
  /** Part of the query key; must be unique per resource. */
  resource: string;
  fetcher: (params: ListParams) => Promise<ListResult<T>>;
  renderRow: (item: T) => React.ReactNode;
  /** Rendered beside the title — a "new record" control, where the page has one. */
  action?: React.ReactNode;
}) {
  const { t } = useI18n();
  const { activeOrgId } = useAuth();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const query = useDebouncedValue(search.trim(), 300);

  // The organisation is part of the key: switching it must not show the
  // previous organisation's rows from cache.
  const result = useQuery({
    queryKey: [resource, activeOrgId, query, page],
    queryFn: () => fetcher({ query, page, limit: PAGE_SIZE }),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PageTitle>{title}</PageTitle>
        {action}
      </div>

      <Input
        value={search}
        placeholder={t("search")}
        onChange={(event) => {
          setSearch(event.target.value);
          // A new search starts from the first page, or an empty page 3 looks
          // like "no results".
          setPage(0);
        }}
        className="max-w-sm"
      />

      <DataState
        isLoading={result.isLoading}
        isError={result.isError}
        isEmpty={(result.data?.items.length ?? 0) === 0}
        onRetry={() => void result.refetch()}
      >
        <Card className="divide-y divide-border">
          {result.data?.items.map((item, index) => (
            <div key={index} className="p-3">
              {renderRow(item)}
            </div>
          ))}
        </Card>
        <Pagination
          page={page}
          limit={PAGE_SIZE}
          total={result.data?.total ?? 0}
          onChange={setPage}
        />
      </DataState>
    </div>
  );
}
